﻿import MainNavigation from "@/components/MainNavigation/MainNavigation";
import { useDispatch, useSelector } from "react-redux";
import { React, useState, useEffect, useCallback, useRef } from "react";
import { Container, Typography, Box } from "@mui/material";
import {
  setSelectedLifecycle,
  selectLifecycle,
  selectConnection,
  getWorkflows,
  getConnectionWorkflows,
  setViewDataPopupOpen,
  selectOpenWorkflowTab,
  setOpenWorkflowTab,
} from "@/features/homepage/homeSlice";
import { selectWorkflow } from "@/features/homepage/workflowManagerSessionSlice";
import { selectIsShrunk } from "@/features/homepage/artifactsMapSlice";
import ViewDataPopup from "./ViewDataPopup/ViewDataPopup";
import {
  selectIsWorkflowStepsOpen,
  setIsWorkflowStepsOpen,
  setWorkflowStepsInfo,
} from "@/features/homepage/workflowManagerSlice";
import ConnectionsBar from "@/components/ConnectionsBar/ConnectionsBar";
import ArtifactsMapPanel from "./Map/ArtifactMapPanel";
import WorkflowManagerTab from "./WorkflowManagerTab/WorkflowManagerTab";
import RecordsTab from "./RecordsTab/RecordsTab";
import MessagePopup from "./MessageTab/MessagePopup";
import WorkflowStepsPopup from "./WorkflowStepsPopup/WorkflowStepsPopup";
import CustomDatePicker from "@/components/CustomDatePicker/CustomDatePicker";
import "./OrderLifecyclePopup/OrderLifecyclePopup.css";
import dayjs from "dayjs";
import { MenuItemEnum } from "@/utils/utils";
import _ from "lodash";
import { ShepTab, ShepTabs } from "@/components/GenericComponents/ShepTab";
import { WorkflowsIcon, RecordsIcon } from "../../ImageRepo";
import axios from "@/features/axiosHelper";
import {
  selectSelectedRows,
  getExportData,
} from "@/features/homepage/recordsSlice";
import {
  setSelectedConnectionPairs,
  selectIsWorkflowSettingsOpen,
  setIsWorkflowSettingsOpen,
} from "@/features/settings/settingsSlice";
import {
  ExportFill,
  ExportLine,
  SettingsIcon,
  SettingsIconHover,
  MessagesLine,
  MessagesUnreadLine,
  MessagesHover,
  MessagesUnreadHover,
} from "../../ImageRepo";
import ShepTooltip from "../GenericComponents/ShepTooltip";
import { createAlert } from "@/features/homepage/alertSliceList";
import WorkflowSettingsPopup from "@/components/Homepage/WorkflowSettingsPopup/WorkflowSettingsPopup";
import {
  loadDateFilter,
  selectHomeDateFilter,
  setHomeDateFilter,
} from "@/features/dateFilterSlice";

const homepageDateFilterKey = "HomepageDateFilter";

const Homepage = () => {
  const [toggle, setToggle] = useState(false);
  const dispatch = useDispatch();
  const workflowPopupOpen = useSelector(selectIsWorkflowStepsOpen);
  const [orderLifecyclePopupOpen, setOrderLifeCyclePopupOpen] = useState(false);
  const workflowSettingsPopup = useSelector(selectIsWorkflowSettingsOpen);
  const [messageOpen, setMessageOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const workflowTabValue = useSelector(selectOpenWorkflowTab);
  const [isArtifactMapExpanded, setIsArtifactMapExpanded] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const isShrunk = useSelector(selectIsShrunk);
  const { startDate, endDate, source } = useSelector(selectHomeDateFilter) || {};

  // Local date state mirrors centralized dateFilter; initial null handled
  const [dates, setDates] = useState([
    startDate,
    endDate,
  ]);

  // Load preferred start date once
  useEffect(() => {
    dispatch(
      loadDateFilter({ area: "home", preferenceKey: homepageDateFilterKey })
    );
  }, [dispatch]);

  // Sync effect so calendar updates when thunk finishes
  useEffect(() => {
    if (startDate && endDate) {
      setDates([startDate, endDate]);
    }
  }, [startDate, endDate]);

  const selectedConnection = useSelector(selectConnection);
  const selectedLifecycle = useSelector(selectLifecycle);
  const selectedWorkflow = useSelector(selectWorkflow);

  var selectedRows = useSelector(selectSelectedRows);


  const handleOpenViewDataPopup = () => {
    dispatch(setViewDataPopupOpen(true));
  };

  const handleCloseViewDataPopup = () => {
    dispatch(setViewDataPopupOpen(false));
  };

  const handleCloseWorkflowPopup = () => {
    dispatch(
      setWorkflowStepsInfo({
        workflowRecordId: "",
        sourceArtifactDictionaryDataId: "",
        connectionPairId: -1,
      })
    );
    dispatch(setIsWorkflowStepsOpen({ isOpen: false }));
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleWorkflowTabChange = (event, newValue) => {
    dispatch(setOpenWorkflowTab({ tabNum: newValue }));

    // heap.io
    switch (newValue) {
      case 0:
        window.heap.track("Home - Select Workflows Tab");
        break;
      case 1:
        window.heap.track("Home - Select Records Tab");
        break;
    }
  };
  const onMessageClose = () => {
    setMessageOpen(false);
  };

  const closeWorkflowSettingsPopup = () => {
    dispatch(setIsWorkflowSettingsOpen({ isOpen: false }));
    dispatch(setSelectedConnectionPairs({ selectedConnectionPairs: [] })); // SHEP-138: Clear selected conn pairs on workflow settings popup close
  };

  // Trigger workflow load only when date filter ready
  useEffect(() => {
    if (startDate && endDate) {
      dispatch(getWorkflows());
      reloadMessageLogo();
    }
  }, [selectedConnection, selectedLifecycle, startDate, endDate, source]);
  useEffect(() => {
    dispatch(getConnectionWorkflows());
  }, [selectedConnection]);

  const reloadMessageLogo = () => {
    if (!startDate || !endDate) return;
    axios
      .get("/message/messageCount", {
        params: {
          startDate: startDate,
          endDate: endDate,
        },
      })
      .then((response) => setUnreadCount(response.data))
      .catch((error) => console.error("Error fetching data:", error));
  };

  const setDateFilterFromDate = (start, end) => {
    if (!start || !end) return;
    dispatch(
      setHomeDateFilter({
        startDate: start,
        endDate: end,
        source: source ? source : "user",
      })
    );
  };

  //useCallback persists the debounced function
  const debouncedSetDateFilter = useCallback(
    _.debounce(setDateFilterFromDate, 1000),
    [source]
  );
  useEffect(() => {
    //delay when making first date selection
    debouncedSetDateFilter(dates[0], dates[1]);
  }, [dates[0]]);
  useEffect(() => {
    //cancel delay and trigger backend call immediately (flush) after second date selection
    debouncedSetDateFilter(dates[0], dates[1]);
    debouncedSetDateFilter.flush();
  }, [dates[1]]);

  return (
    <>
      <Container sx={{ backgroundColor: "var(--disabled)" }}>
        <Container
          sx={{
            position: "sticky",
            top: "0",
            zIndex: "10",
          }}
        >
          <MainNavigation selected={MenuItemEnum.Homepage} />
          <ConnectionsBar />
        </Container>
        <Container className="content-container">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              borderBottom: "1.5px solid var(--primary-main)",
              marginBottom: "37px",
            }}
          >
            <ShepTabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="lifecycle tabs"
            >
              <ShepTab
                label={"Order Lifecycle"}
                id="tab-0"
                aria-controls="tabpanel-0"
                sx={{ width: "310px" }}
                onClick={() =>
                  dispatch(setSelectedLifecycle({ selectedLifecycle: 1010 }))
                }
              />
              <ShepTab
                label={"Inventory Lifecycle"}
                id="tab-1"
                aria-controls="tabpanel-1"
                sx={{ width: "310px" }}
                onClick={() =>
                  dispatch(setSelectedLifecycle({ selectedLifecycle: 1030 }))
                }
              />
            </ShepTabs>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                flex: 1,
                marginTop: "auto",
                marginBottom: "5px",
              }}
            >
              <CustomDatePicker
                dates={dates}
                setDates={setDates}
                onApply={() => {}}
                preferenceKey={homepageDateFilterKey}
                heapIOParentPage="Workflow Manager"
              />
            </div>
          </div>
          <Box
            className="content-box"
            sx={{ display: "flex", flexDirection: "row" }}
          >
            <Box
              sx={{
                display: isShrunk ? "none" : "block",
                flex: 1,
              }}
            >
              <ArtifactsMapPanel />
            </Box>
            <Box
              sx={{
                flex: isShrunk ? 1 : "auto",
                marginLeft: isShrunk ? "0" : "20px",
                width: "100%",
                backgroundColor: "var(--disabled)",
                minWidth: "0",
              }}
            >
              <Container
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1.5px solid var(--primary-main)",
                  borderRadius: "32px 32px 0px 0px",
                }}
              >
                <ShepTabs
                  value={workflowTabValue}
                  onChange={handleWorkflowTabChange}
                  aria-label="workflow tabs"
                >
                  <ShepTab
                    icon={<WorkflowsIcon />}
                    label={"Workflows"}
                    sx={{ width: 225 }}
                  />
                  <ShepTab
                    icon={<RecordsIcon />}
                    label={"Records"}
                    iconPosition="start"
                    sx={{ width: 225 }}
                  />
                </ShepTabs>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {selectedWorkflow !== 0 && <SettingsButton />}
                  {workflowTabValue === 1 && <ExportButton />}
                  <MessagesButton
                    unreadCount={unreadCount}
                    onMessageClicked={() => {
                      // heap.io
                      window.heap.track("Messages - Open Screen");

                      setMessageOpen(true);
                    }}
                  />
                </Box>
              </Container>
              <Box>
                {workflowTabValue === 0 && <WorkflowManagerTab />}
                {workflowTabValue === 1 && <RecordsTab />}
              </Box>
            </Box>
          </Box>
          <WorkflowStepsPopup
            open={workflowPopupOpen}
            onClose={handleCloseWorkflowPopup}
          />
          <MessagePopup open={messageOpen} onClose={onMessageClose} />
          <ViewDataPopup
            open={handleOpenViewDataPopup}
            onClose={handleCloseViewDataPopup}
          />
          {/* Note: We need to only render the popup component when it's open, as rendering without a "open" check 
        would messup as the state it needs would not be available by then. */}
          {workflowSettingsPopup ? (
            <>
              <WorkflowSettingsPopup
                open={workflowSettingsPopup}
                onClose={closeWorkflowSettingsPopup}
              />
            </>
          ) : null}
        </Container>
      </Container>
    </>
  );
};

const ExportButton = () => {
  const dispatch = useDispatch();
  var selectedRows = useSelector(selectSelectedRows);
  const selectedConnection = useSelector(selectConnection);
    const { startDate, endDate } = useSelector(selectHomeDateFilter) || {};
  const workflowTabValue = useSelector(selectOpenWorkflowTab);

  const handleExport = () => {
    // heap.io
    window.heap.track("Records - Export");

    if (workflowTabValue === 1) {
      dispatch(
        createAlert({
          status: "Info",
          alertType: "single",
          message: "Export In Progress",
        })
      );
      dispatch(
        getExportData({
          selectedRows: selectedRows,
          selectedConnection: selectedConnection?.connectionId ?? -1,
          startDate: startDate,
          endDate: endDate,
        })
      )
        .unwrap()
        .then((result) => {
          if (result) {
            dispatch(
              createAlert({
                status: "Success",
                alertType: "single",
                message: "Export Successful",
              })
            );
          }
        })
        .catch((error) => {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "single",
              message: "Export Failed. " + error,
            })
          );
        });
    } else {
      dispatch(
        createAlert({
          status: "Info",
          alertType: "single",
          message: "Please navigate to the Records tab to export the data",
        })
      );
    }
  };

  return (
    <DynamicIconButton
      tooltip="Export"
      src={ExportLine}
      hoverSrc={ExportFill}
      onClick={handleExport}
      height={24}
    />
  );
};

const DynamicIconButton = ({
  tooltip,
  src,
  hoverSrc,
  onClick,
  height = 18,
  className,
  style,
  sx,
}) => {
  const [mouseIn, setMouseIn] = useState(false);

  const onMouseEnter = () => setMouseIn(true);
  const onMouseLeave = () => setMouseIn(false);

  const handleClick = (event) => {
    if (onClick && typeof onClick === "function") {
      onClick(event);
      event.stopPropagation();
    }
  };

  const getSrc = () => (mouseIn && hoverSrc ? hoverSrc : src);

  const imageElement = (
    <img
      className={className}
      src={getSrc()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={handleClick}
      height={height}
      style={style}
      sx={sx}
    />
  );

  return tooltip ? (
    <ShepTooltip title={tooltip} placement="bottom" style={style} sx={sx}>
      {imageElement}
    </ShepTooltip>
  ) : (
    imageElement
  );
};

const SettingsButton = () => {
  const dispatch = useDispatch();

  const handleClick = () => {
    dispatch(setIsWorkflowSettingsOpen({ isOpen: true }));

    // heap.io
    window.heap.track("Workflow Settings - Open via Gear");
  };

  return (
    <DynamicIconButton
      tooltip="Settings"
      src={SettingsIcon}
      hoverSrc={SettingsIconHover}
      onClick={handleClick}
      height={31}
    />
  );
};

const MessagesButton = ({ unreadCount, onMessageClicked }) => {
  return (
    <DynamicIconButton
      tooltip="Messages"
      src={unreadCount ? MessagesUnreadLine : MessagesLine}
      hoverSrc={unreadCount ? MessagesUnreadHover : MessagesHover}
      onClick={onMessageClicked}
      height={31}
    />
  );
};

export default Homepage;
